import {
  ListItem,
  ProductThumbnail,
  QuantityBadge,
  Text,
} from "@towersystems/roam-common-native";
import { Colors, Spacings, View } from "react-native-ui-lib";
import StockCountItemModel from "../../../../utilities/data/model/StockCountItem";
import StockCountLineModel from "../../../../utilities/data/model/StockCountLine";
import { useEffect, useState } from "react";
import { SyncState } from "../../../../utilities/data/constants";
import Product from "../../../../utilities/data/model/Product";
import { isNumber } from "../../../../utilities/is-number";
import { ValueOf } from "../../../../utilities/type-helpers";
import { useIsMounted } from "../../../../utilities/use-is-mounted";
import React from "react";
import { Feather, Ionicons } from "@expo/vector-icons";
import { ActivityIndicator } from "react-native";
import { useCurrentCountContext } from "../../../../utilities/stock-count/hooks";
import {
  StockCountItemStatus,
  StockCountSource,
} from "@towersystems/roam-common/lib/generated-types";

export const ListItemVariants = {
  FULL: "full",
  EXPECTED: "expected",
  COUNTED: "counted",
} as const;

export type BaseRendererProps = {
  selected?: boolean;
  variant?: ValueOf<typeof ListItemVariants>;
  onPress?: () => void | Promise<void>;
  compact?: boolean;
};

export type StockCountItemListItemRendererProps = BaseRendererProps & {
  stockCountLine: StockCountLineModel;
  stockCountItems: StockCountItemModel[];
  product?: Product;
};

function StockCountItemListItemRendererInner({
  selected,
  stockCountLine,
  product,
  stockCountItems = [],
  compact,
  variant = ListItemVariants.FULL,
  onPress,
}: StockCountItemListItemRendererProps) {
  const { stockCount } = useCurrentCountContext();

  const isInternalCount = stockCount.source == StockCountSource.INTERNAL;

  const isMounted = useIsMounted();
  const [numFailedExports, setNumFailedExports] = useState<number>(0);

  const colorStyles = {
    color: Colors.Text,
  };

  const failedToSync = stockCountItems.some(
    (count) => count.numSyncAttempts >= 1 && count.syncState == SyncState.UPLOAD
  );

  const isSyncing = stockCountItems.some(
    (count) => count.syncState === SyncState.UPLOAD
  );

  const exportIsComplete = stockCountItems.every(
    (count) => count.status === StockCountItemStatus.EXPORTED
  );

  const columnWidth = compact ? 70 : 100;

  const columnStyle = {
    alignItems: "center",
    width: columnWidth,
    justifyContent: "flex-end",
  } as const;

  const count = stockCountItems.reduce((acc, item) => {
    if (isNumber(item.counted)) {
      return acc + (item.counted || 0);
    }
    return acc;
  }, 0);

  const asExpected =
    isNumber(count) &&
    isNumber(stockCountLine.expected) &&
    stockCountLine.expected === count;

  React.useEffect(() => {
    const subscription = stockCountLine.items.observe().subscribe((_items) => {
      stockCountLine
        .getFailedExports()
        .then((failedExports) => {
          setNumFailedExports(failedExports.length);
        })
        .catch(console.log);
    });

    return () => {
      if (isMounted.current) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const loadingIcon = isSyncing ? (
    <View style={{ marginRight: Spacings.s5 }}>
      <ActivityIndicator size="small" />
    </View>
  ) : null;

  return (
    <ListItem
      selected={selected}
      onPress={onPress}
      style={{
        backgroundColor: selected ? "#EBEBEB" : "transparent",
      }}
    >
      <View
        flex
        row
        style={{
          paddingVertical: Spacings.s4,
        }}
      >
        <View
          row
          style={{ alignItems: "center", flexBasis: "100%", flexShrink: 1 }}
        >
          <View style={{ marginRight: Spacings.s4 }}>
            <ProductThumbnail imageUrl={product?.imageUrl} />
          </View>
          <View style={{ flexShrink: 1 }}>
            <Text
              variant="heading1"
              style={colorStyles}
              numberOfLines={1}
              ellipsizeMode="head"
            >
              {product?.name || stockCountLine.name}
            </Text>
            <Text
              variant="heading5"
              color="subdued"
              numberOfLines={1}
              ellipsizeMode="head"
            >
              {product?.stockNumber || stockCountLine.barcode}
            </Text>
          </View>
        </View>
        {variant !== ListItemVariants.COUNTED && (
          <View row style={columnStyle}>
            <QuantityBadge
              quantity={stockCountLine.expected || 0}
              textStyles={{
                color: selected ? Colors.white : Colors.Text,
              }}
              style={{
                backgroundColor: selected
                  ? Colors.primary
                  : "rgb(216, 217, 219)",
              }}
            />
          </View>
        )}
        {variant !== ListItemVariants.EXPECTED && (
          <View row style={columnStyle}>
            <QuantityBadge
              quantity={count}
              variant={asExpected ? "success" : "default"}
              textStyles={{
                color: selected ? Colors.white : Colors.Text,
              }}
              style={{
                backgroundColor: selected
                  ? Colors.primary
                  : "rgb(216, 217, 219)",
              }}
            />
          </View>
        )}
        <View row style={columnStyle}>
          {loadingIcon}
          {false == isInternalCount ? (
            !loadingIcon && (failedToSync || !!numFailedExports) ? (
              <View style={{ marginRight: Spacings.s5 }}>
                <Text variant="heading1" style={colorStyles}>
                  <Ionicons
                    name="warning"
                    size={18}
                    color={Colors.destructive}
                  />
                </Text>
              </View>
            ) : (
              <View style={{ marginRight: Spacings.s5 }}>
                {exportIsComplete ? (
                  <Text variant="heading1" style={colorStyles}>
                    <Feather name="check" size={18} color={Colors.success} />
                  </Text>
                ) : (
                  <Text variant="heading1" style={colorStyles}>
                    <ActivityIndicator size="small" />
                  </Text>
                )}
              </View>
            )
          ) : null}

          {isInternalCount === true
            ? !loadingIcon &&
              (failedToSync ? (
                <View style={{ marginRight: Spacings.s5 }}>
                  <Text variant="heading1" style={colorStyles}>
                    <Ionicons
                      name="warning"
                      size={18}
                      color={Colors.destructive}
                    />
                  </Text>
                </View>
              ) : (
                <View style={{ marginRight: Spacings.s5 }}>
                  <Text variant="heading1" style={colorStyles}>
                    <Feather name="check" size={18} color={Colors.success} />
                  </Text>
                </View>
              ))
            : null}
        </View>
      </View>
    </ListItem>
  );
}

export function StockCountItemListItemRenderer(
  props: StockCountItemListItemRendererProps
) {
  const [productState, setProductState] = useState<Product | undefined>(
    undefined
  );
  const { stockCountLine } = props;

  useEffect(() => {
    if (product) return;
    stockCountLine.product
      .fetch()
      .then(setProductState)
      .catch(() => {
        setProductState(undefined);
      });
  }, [stockCountLine]);

  const product = productState || props.product;

  if (!product && stockCountLine.product.id) return null;

  return <StockCountItemListItemRendererInner {...props} {...{ product }} />;
}
